// The Meeting tab: the agenda for the meeting the room is in (kanban 108573ba).
//
// Owner, review vhztut (2026-10-07): "agenda for today's alpha meeting ...
// make sure it properly understands which meeting it should be talking
// about ... we need to do a UI pass on it too." Five concepts went through a
// visual, a mouse-only and a keyboard-only pass; the owner picked "Agenda
// and evidence" (review 0afff479): "list on the left, the words on the right
// ... it should have like an arrow key thing, because someone's sharing this
// on the screen ... or some kind of way to click for, like, complete it."
//
// So: a two-pane view. Left, the numbered agenda for one meeting series; the
// kind of attention first on each line, a real Late / At risk / Stuck badge,
// a quote marker, and a tick when the room has covered it. Right, the
// selected item: why it is on the agenda, who holds it, every question due
// in the room (answer or dismiss it right here, as on the questions tab, or
// open it in the page's question pop-up when the page has one),
// the words behind it with Open source into the transcript, the summary, and
// the way into the initiative. Up/Down (or J/K) move the selection so a
// shared screen follows the chair; Enter opens the initiative; Space or C
// marks the selected item covered and moves on to the next uncovered one.
// Covered marks are kept per meeting day in the browser (meetingCovered.ts).
// On a phone the list is the screen and an item opens the detail with a Back.
//
// Which meeting: a picker over the series THIS PERSON may open (the backend
// gate, GET /my-roadmap/meetings: leadership sees every series, a team
// member their own team's), opening on the soonest one by weekday. The same
// panel serves the leadership /roadmap tab and the portal's Meetings tab;
// the portal passes no onSelect (there is no initiative page to open) and a
// canRespondTo so only the questions addressed to the reader get controls.
// The agenda is deterministic today (meetingSeries.agendaForSeries); when
// Merlin writes agendas per run, its lines take the top of this list.
//
// Two sections down the left (kanban c3097c0a, admin 2026-10-21 on the
// landing view: "why does this only show 4 things? aren't there more?";
// owner on review 4yi1x3: "a critical section for what is showing there now
// and then still show the rest ... arranged and color coded by
// importance/urgency"). "Needs the room" is the numbered agenda; "The rest
// of <team>" is every other active initiative of this meeting, by urgency
// (coming up, on track, no date), each opening the initiative. Every row
// carries its urgency as a colour down the list. The head says the rule
// once, what the view left off the room entirely (other meetings, done or
// parked), and the way to every initiative.
//
// Every control carries a data-action so the user-action recorder names the
// row; keyboard moves have no click, so they are recorded by hand.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ClarifyingQuestion, QuestionIdentity } from '../../services/roadmapReports';
import { AGENDA_KIND_HINTS, AGENDA_KIND_LABELS, AGENDA_RULE, agendaForSeries, itemQuotes, meetingLabel, REST_TIER_HINTS, REST_TIER_LABELS, sameTeams, seriesById, seriesForTeams, type AgendaItem, type MeetingInitiative, type MeetingSeries, type SeriesId } from './meetingSeries';
import { recordAction } from '../../services/guruRecorderBridge';
import { nextUncovered, readCovered, toggleCovered, writeCovered } from './meetingCovered';
import { decideByFromQuestion, questionAddressees, questionBody, STATUS_LABELS, type TeamColors } from './roadmapViewUtils';
import { fmtDay, fmtDayYear, isoDay, parseDay } from './roadmapTimelineUtils';
import { EvidenceList, type OpenSourceFn } from './EvidenceList';
import { QuestionResponseControls, type RespondFn, type WithdrawFn } from './QuestionResponseControls';
import type { ModalSection } from './TimelineChart';
import styles from './RoadmapV2.module.css';

interface Props {
  /** The initiatives on the view: the page's filters applied, when it has any (owner, 2026-10-12). */
  initiatives: MeetingInitiative[];
  /** Every initiative, so a blocker outside the filter is still named; defaults to `initiatives`. */
  allInitiatives?: MeetingInitiative[];
  questions: ClarifyingQuestion[];
  /** ISO day, injectable for tests. */
  today: string;
  /** The series this person may open (the backend gate), registry order. */
  seriesList: MeetingSeries[];
  series: SeriesId;
  onSeriesChange: (id: SeriesId) => void;
  teamColors: TeamColors;
  /** Open the initiative (the leadership page's modal); omitted → titles are plain text. */
  onSelect?: (key: string, section?: ModalSection) => void;
  /** Open ONE question in the page's question pop-up (owner ask 2026-10-12: a
   * question line opens the pop-up, not the tab); omitted → the question is plain text. */
  onOpenQuestion?: (id: QuestionIdentity) => void;
  /** Jump to the questions tab; omitted → no "See all open questions". */
  onOpenQuestions?: () => void;
  /** Jump to the list of every initiative; omitted → the count of what was left off has no link. */
  onOpenList?: () => void;
  onOpenSource?: OpenSourceFn;
  /** Answer / dismiss a question from the pane; omitted → read-only. */
  onRespond?: RespondFn;
  onWithdraw?: WithdrawFn;
  /** Which questions this reader may answer; omitted → all of them (leadership). */
  canRespondTo?: (q: ClarifyingQuestion) => boolean;
  /** The day the roadmap was last written by a run, when known. */
  asOf?: string | null;
  /** The page's Team filter, when it has one. A filter that is not the
   * shown series' own team narrows the agenda further, and the head says so
   * instead of intersecting in silence (kanban 4d581b21). */
  filterTeams?: readonly string[];
  /** Take the Team filter off, leaving the series as the only narrowing; omitted → the note has no button. */
  onClearTeams?: () => void;
}

const HEALTH_BADGE: Partial<Record<MeetingInitiative['health'], string>> = { late: 'Late', at_risk: 'At risk', stuck: 'Stuck' };

const NO_SERIES: MeetingSeries = { id: '', name: '', weekday: 0, time: '', teams: [], room: [] };

/** What the room holds for one meeting (series + day): the chair's
 * selection, the phone's open detail, and the covered marks (null = not read
 * from storage yet). Keyed on the meeting so another meeting starts clean. */
interface RoomState {
  meetingKey: string;
  selectedKey: string | null;
  detailOpen: boolean;
  covered: Set<string> | null;
}
const freshRoom = (meetingKey: string): RoomState => ({ meetingKey, selectedKey: null, detailOpen: false, covered: null });

export function MeetingPanel({ initiatives, allInitiatives, questions, today, seriesList, series, onSeriesChange, teamColors, onSelect, onOpenQuestion, onOpenQuestions, onOpenList, onOpenSource, onRespond, onWithdraw, canRespondTo, asOf = null, filterTeams = [], onClearTeams }: Props) {
  const todayDate = useMemo(() => parseDay(today) ?? new Date(), [today]);
  const picked = seriesById(seriesList, series);
  const current = picked ?? NO_SERIES;
  // The Team filter narrows this view like every other; when it is not the
  // series' own team the agenda is an intersection, and the head names it.
  const narrowedTo = filterTeams.length > 0 && !sameTeams(filterTeams, current.teams) ? filterTeams.join(', ') : null;
  // One filtered team that no series claims (kanban e3c01806: the admin read
  // "Narrowed ... to Sigma team" as an empty Sigma agenda): say there is no
  // such meeting, not that this one was narrowed.
  const noMeetingOfItsOwn = narrowedTo !== null && filterTeams.length === 1 && seriesForTeams(seriesList, filterTeams) === null;
  const agenda = useMemo(() => agendaForSeries({ series: current, initiatives, all: allInitiatives, questions, today: todayDate }), [current, initiatives, allInitiatives, questions, todayDate]);
  const meetingDay = isoDay(agenda.date);
  const year = todayDate.getFullYear();
  const keys = useMemo(() => agenda.items.map((i) => i.key), [agenda.items]);

  // Per-meeting state (selection, phone detail, covered marks) is stored
  // with the meeting it belongs to and derived in render, so switching
  // meetings starts clean without a setState in an effect.
  const meetingKey = `${series}|${meetingDay}`;
  const [room, setRoom] = useState<RoomState>(() => freshRoom(meetingKey));
  const live = room.meetingKey === meetingKey ? room : freshRoom(meetingKey);
  const selectedKey = live.selectedKey;
  const detailOpen = live.detailOpen;
  const covered = live.covered ?? readCovered(series, meetingDay);
  const update = useCallback((patch: (base: RoomState) => Partial<RoomState>) => {
    setRoom((r) => { const base = r.meetingKey === meetingKey ? r : freshRoom(meetingKey); return { ...base, ...patch(base) }; });
  }, [meetingKey]);
  const setDetailOpen = (open: boolean) => update(() => ({ detailOpen: open }));
  // Selection follows the chair.
  const selectedIndex = Math.max(0, keys.indexOf(selectedKey ?? ''));
  const selected: AgendaItem | null = agenda.items[selectedIndex] ?? null;

  const rowRefs = useRef(new Map<string, HTMLButtonElement>());
  // The detail pane scrolls inside its own box (it is pinned beside the list), so a new
  // selection must start at its top, not wherever the last item was left. A DOM scroll
  // in an effect is fine; it is state that the file keeps out of effects.
  const detailRef = useRef<HTMLElement>(null);
  const selectedForScroll = selected?.key ?? null;
  useEffect(() => { if (detailRef.current) detailRef.current.scrollTop = 0; }, [selectedForScroll]);

  const select = (index: number, focusRow = true) => {
    const key = keys[Math.max(0, Math.min(keys.length - 1, index))];
    if (!key) return;
    update(() => ({ selectedKey: key }));
    if (focusRow) {
      rowRefs.current.get(key)?.focus();
      // A key move has no click for the recorder's DOM listener to name.
      recordAction('agenda_key_move', { targets: { initiative_key: key }, values: { mode: 'keyboard' }, outcome: 'none' });
    }
  };

  const markCovered = (key: string, advance: boolean) => {
    update((base) => {
      const next = toggleCovered(base.covered ?? readCovered(series, meetingDay), key);
      writeCovered(series, meetingDay, next);
      let selectedKey = base.selectedKey;
      if (advance && next.has(key)) {
        const at = keys.indexOf(key);
        const to = nextUncovered(keys, next, at);
        if (to !== at) selectedKey = keys[to];
      }
      return { covered: next, selectedKey };
    });
  };

  // Keys for the room: Up/Down or J/K move, Enter opens the initiative,
  // Space (on a row) or C covers the selected item. Typing in a box is left alone.
  const onKeyDown = (e: React.KeyboardEvent) => {
    const t = e.target instanceof HTMLElement ? e.target : null;
    if (t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)) return;
    if (!selected) return;
    const onRow = t?.dataset.agendaTitle !== undefined;
    if (e.key === 'ArrowDown' || e.key === 'j' || e.key === 'ArrowRight') { e.preventDefault(); select(selectedIndex + 1); return; }
    if (e.key === 'ArrowUp' || e.key === 'k' || e.key === 'ArrowLeft') { e.preventDefault(); select(selectedIndex - 1); return; }
    if (e.key === 'Enter' && onRow && onSelect) { e.preventDefault(); onSelect(selected.key); return; }
    if ((e.key === ' ' && onRow) || e.key === 'c' || e.key === 'C') {
      e.preventDefault();
      markCovered(selected.key, true);
      recordAction('agenda_cover', { targets: { initiative_key: selected.key }, values: { mode: 'keyboard' }, outcome: 'none' });
    }
  };

  const canRespond = Boolean(onRespond && onWithdraw);
  const mayAnswer = (q: ClarifyingQuestion) => canRespond && (canRespondTo ? canRespondTo(q) : true);
  const coveredCount = keys.filter((k) => covered.has(k)).length;
  const withoutQuote = agenda.items.filter((i) => itemQuotes(i).length === 0).length;

  const ask = (q: ClarifyingQuestion) => {
    const byDay = parseDay(decideByFromQuestion(q.question, year));
    return (
      <div key={q.question} className={styles.agendaAsk} data-testid="agenda-ask">
        <div>
          <span className={styles.agendaAskWho}>{questionAddressees(q).join(' / ')}:</span>{' '}
          {onOpenQuestion ? (
            <button
              type="button"
              className={styles.agendaAskBtn}
              onClick={() => onOpenQuestion({ initiative_key: q.initiative_key, question: q.question })}
              title="Open this question"
              data-action="agenda_open_question"
              data-target-initiative-key={q.initiative_key}
            >
              {questionBody(q)}
            </button>
          ) : (
            questionBody(q)
          )}
          {byDay && <span className={styles.agendaAskBy}>decide by {fmtDay(byDay)}</span>}
        </div>
        {mayAnswer(q) && onRespond && onWithdraw ? (
          <QuestionResponseControls identity={{ initiative_key: q.initiative_key, question: q.question }} response={q.response} onRespond={onRespond} onWithdraw={onWithdraw} />
        ) : q.response ? (
          <div className={styles.muted}>{q.response.kind === 'dismissed' ? 'Dismissed' : 'Answered'}, waiting for Merlin.</div>
        ) : null}
      </div>
    );
  };

  const quotes = selected ? itemQuotes(selected) : [];
  const badge = selected ? HEALTH_BADGE[selected.initiative.health] : undefined;

  // What the view left off this room entirely, in one sentence:
  // "Not listed: 38 for other meetings and 6 done or parked."
  const { offSeries, closed } = agenda.omitted;
  const leftOff = offSeries + closed;
  const roomWord = current.teams.length ? current.teams.join(' / ') : 'the roadmap';
  const plural = (n: number) => (n === 1 ? '' : 's');
  const leftOffParts = [
    offSeries ? `${offSeries} for other meetings` : null,
    closed ? `${closed} done or parked` : null,
  ].filter((x): x is string => x !== null);
  const leftOffLine = leftOffParts.join(' and ');
  // "See all N": the whole roadmap, not the Team-filtered view the pick narrows to.
  const everyone = allInitiatives ?? initiatives;
  const inRoom = agenda.items.length + agenda.rest.length;
  const priorityTag = (i: MeetingInitiative) => (i.priority === 'top' || i.priority === 'high') && <span className={styles.priorityTag} data-priority={i.priority}>{i.priority === 'top' ? 'Top priority' : 'High priority'}</span>;

  if (!picked) {
    return (
      <section className={styles.agenda} aria-label="Meeting view">
        <p className={styles.meetingEmpty} data-testid="agenda-none">
          None of the roadmap's meetings lists you yet. When the people directory puts you on a team, or a meeting names you in its room, its agenda shows up here.
        </p>
      </section>
    );
  }

  return (
    <section className={`${styles.agenda} ${detailOpen ? styles.agendaDetailOpen : ''}`} aria-label="Meeting view" onKeyDown={onKeyDown}>
      <div className={styles.agendaHead}>
        <div className={styles.agendaTitleRow}>
          <h2 className={styles.agendaTitle}>Agenda for</h2>
          <div className={styles.agendaSeries} role="group" aria-label="Which meeting">
            {seriesList.map((s) => (
              <button key={s.id} type="button" className={`${styles.agendaSeriesBtn} ${s.id === current.id ? styles.agendaSeriesOn : ''}`} aria-pressed={s.id === current.id} onClick={() => onSeriesChange(s.id)} data-action="agenda_series" data-target-series={s.id}>
                {s.name}
              </button>
            ))}
          </div>
        </div>
        <p className={styles.agendaSub} data-testid="agenda-sub">
          {meetingLabel(current, todayDate)} · {agenda.items.length} item{agenda.items.length === 1 ? '' : 's'} · about {agenda.totalMinutes} minute{agenda.totalMinutes === 1 ? '' : 's'}
          {agenda.items.length > 0 && <> · <span data-testid="agenda-covered">{coveredCount} of {agenda.items.length} covered</span></>}
          {asOf && <> · from Merlin's run of {asOf}</>}
        </p>
        {(leftOff > 0 || agenda.rest.length > 0) && (
          <p className={styles.agendaScope} data-testid="agenda-scope">
            {[inRoom > 0 ? AGENDA_RULE : null, leftOff > 0 ? `Not listed: ${leftOffLine}.` : null].filter(Boolean).join(' ')}
            {onOpenList && <> <button type="button" className={styles.linkBtn} onClick={onOpenList} data-action="agenda_open_list">See all {everyone.length} initiative{plural(everyone.length)} &rarr;</button></>}
          </p>
        )}
        {narrowedTo && (
          <p className={styles.agendaSub} data-testid="agenda-narrowed" data-reason={noMeetingOfItsOwn ? 'no-series' : 'narrowed'}>
            {noMeetingOfItsOwn
              ? <>{narrowedTo} has no meeting of its own yet: showing {current.name}'s items tagged with it.</>
              : <>Narrowed by the Team filter to {narrowedTo}: only {current.name}'s items tagged with {filterTeams.length === 1 ? 'that team' : 'those teams'} are listed.</>}
            {onClearTeams && <> <button type="button" className={styles.linkBtn} onClick={onClearTeams} data-action="agenda_clear_teams">Show the whole {current.name} agenda</button></>}
          </p>
        )}
      </div>

      {inRoom === 0 ? (
        <p className={styles.meetingEmpty} data-testid="agenda-empty">
          Nothing on the agenda for {current.name}: none of its items is late, waiting, or carrying a question due this week.
        </p>
      ) : (
        <div className={styles.agendaPanes}>
          <nav className={styles.agendaListPane} aria-label={`Agenda for ${current.name}`}>
            <h3 className={styles.agendaSection} data-testid="agenda-needs-head">Needs the room <span className={styles.meetingCount}>{agenda.items.length}</span></h3>
            {agenda.items.length === 0 && (
              <p className={styles.agendaFoot} data-testid="agenda-empty">
                Nothing needs the room for {current.name}: none of its items is late, waiting, or carrying a question due this week.
              </p>
            )}
            <ol className={styles.agendaList}>
              {agenda.items.map((item, n) => {
                const isSel = item.key === selected?.key;
                const isCovered = covered.has(item.key);
                const hb = HEALTH_BADGE[item.initiative.health];
                return (
                  <li key={item.key} className={`${styles.agendaRow} ${isSel ? styles.agendaRowOn : ''} ${isCovered ? styles.agendaRowCovered : ''}`} data-testid={`agenda-item-${item.key}`} data-urgency={item.kind}>
                    <button
                      type="button"
                      ref={(el) => { if (el) rowRefs.current.set(item.key, el); else rowRefs.current.delete(item.key); }}
                      className={styles.agendaRowBtn}
                      data-agenda-title
                      data-action="agenda_row_select"
                      data-target-initiative-key={item.key}
                      aria-current={isSel ? 'true' : undefined}
                      onClick={() => { select(n, false); setDetailOpen(true); }}
                      title={onSelect ? 'Select; Enter opens the initiative; Space marks it covered' : 'Select; Space marks it covered'}
                    >
                      <span className={styles.agendaNum} aria-hidden="true">{isCovered ? '✓' : n + 1}</span>
                      <span className={styles.agendaRowMain}>
                        <span className={styles.agendaTags}>
                          <span className={`${styles.agendaKind} ${styles[`agendaKind_${item.kind}`]}`} title={AGENDA_KIND_HINTS[item.kind]}>{AGENDA_KIND_LABELS[item.kind]}</span>
                          {hb && <span className={styles.agendaBad}>{hb}</span>}
                          {priorityTag(item.initiative)}
                          {itemQuotes(item).length > 0 && <span className={styles.agendaHasQuote} title="Has the words behind it">&ldquo;&rdquo;</span>}
                          <span className={styles.agendaMins}>{item.minutes} min</span>
                        </span>
                        <span className={styles.agendaRowTitle}>{item.title}</span>
                        <span className={styles.agendaRowMeta}>{item.reasons[0]}{item.who ? ` · ${item.who}` : ''}</span>
                      </span>
                    </button>
                    <label className={styles.agendaCheck} title="Covered in the meeting">
                      <input type="checkbox" checked={isCovered} onChange={() => markCovered(item.key, false)} aria-label={`Covered: ${item.title}`} data-action="agenda_cover_toggle" data-target-initiative-key={item.key} />
                    </label>
                  </li>
                );
              })}
            </ol>
            {agenda.alsoForTheRoom.length > 0 && (
              <div className={styles.agendaAlso} data-testid="agenda-also">
                <h3>Also for people in the room <span className={styles.meetingCount}>{agenda.alsoForTheRoom.length}</span></h3>
                <ul>
                  {agenda.alsoForTheRoom.map((q) => (
                    <li key={`${q.initiative_key}:${q.question}`}>
                      {onSelect ? <button type="button" className={styles.linkBtn} onClick={() => onSelect(q.initiative_key, 'questions')} data-action="agenda_open_initiative" data-target-initiative-key={q.initiative_key}>{q.title}</button> : <strong>{q.title}</strong>}
                      <div className={styles.agendaRowMeta}><span className={styles.agendaAskWho}>{questionAddressees(q).join(' / ')}:</span> {questionBody(q)}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {agenda.rest.length > 0 && (
              <section className={styles.agendaRest} data-testid="agenda-rest" aria-label={`The rest of ${roomWord}`}>
                <h3 className={styles.agendaSection}>The rest of {roomWord} <span className={styles.meetingCount}>{agenda.rest.length}</span></h3>
                <ul className={styles.agendaList}>
                  {agenda.rest.map((r) => {
                    const t = parseDay(r.target);
                    const meta = [r.who ?? 'nobody named', t ? `due ${fmtDay(t)}` : 'no target date', r.laterQuestions.length ? `${r.laterQuestions.length} question${plural(r.laterQuestions.length)} for later` : null].filter(Boolean).join(' · ');
                    const body = (
                      <span className={styles.agendaRowMain}>
                        <span className={styles.agendaTags}>
                          <span className={`${styles.agendaKind} ${styles[`agendaKind_${r.tier}`]}`} title={REST_TIER_HINTS[r.tier]}>{REST_TIER_LABELS[r.tier]}</span>
                          {priorityTag(r.initiative)}
                        </span>
                        <span className={styles.agendaRowTitle}>{r.title}</span>
                        <span className={styles.agendaRowMeta}>{meta}</span>
                      </span>
                    );
                    return (
                      <li key={r.key} className={`${styles.agendaRow} ${styles.agendaRestRow}`} data-testid={`agenda-rest-${r.key}`} data-urgency={r.tier}>
                        {onSelect
                          ? <button type="button" className={styles.agendaRowBtn} onClick={() => onSelect(r.key)} title="Open the initiative" data-action="agenda_open_initiative" data-target-initiative-key={r.key}>{body}</button>
                          : <div className={styles.agendaRowBtn}>{body}</div>}
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
            {withoutQuote > 0 && (
              <p className={styles.agendaFoot} data-testid="agenda-noquote-note">
                {withoutQuote === agenda.items.length ? 'No item has a quote yet' : `${withoutQuote} item${withoutQuote === 1 ? ' has' : 's have'} no quote yet`}; Merlin adds the words behind each one on its next run.
              </p>
            )}
            {onOpenQuestions && <button type="button" className={styles.meetingMore} onClick={onOpenQuestions} data-action="agenda_all_questions">See all open questions</button>}
          </nav>

          {selected && (
            <article ref={detailRef} className={styles.agendaDetail} aria-label={`Selected: ${selected.title}`} data-testid="agenda-detail">
              <div className={styles.agendaDetailTop}>
                <button type="button" className={`${styles.btnGhost} ${styles.agendaBack}`} onClick={() => setDetailOpen(false)} data-action="agenda_back">&larr; Agenda</button>
                <span className={styles.agendaDetailPos} data-testid="agenda-pos">{selectedIndex + 1} of {agenda.items.length}</span>
                <div className={styles.agendaDetailNav}>
                  <button type="button" className={styles.btnGhost} onClick={() => select(selectedIndex - 1, false)} disabled={selectedIndex === 0} aria-label="Previous item" data-action="agenda_prev">&uarr; Previous</button>
                  <button type="button" className={styles.btnGhost} onClick={() => select(selectedIndex + 1, false)} disabled={selectedIndex >= agenda.items.length - 1} aria-label="Next item" data-action="agenda_next">Next &darr;</button>
                  <button type="button" className={`${styles.agendaCoverBtn} ${covered.has(selected.key) ? styles.agendaCoverBtnOn : ''}`} onClick={() => markCovered(selected.key, true)} aria-pressed={covered.has(selected.key)} data-testid="agenda-cover" data-action="agenda_cover" data-target-initiative-key={selected.key}>
                    {covered.has(selected.key) ? '✓ Covered' : 'Mark covered'}
                  </button>
                </div>
              </div>
              <div className={styles.agendaTags}>
                <span className={`${styles.agendaKind} ${styles[`agendaKind_${selected.kind}`]}`}>{AGENDA_KIND_LABELS[selected.kind]}</span>
                {badge && <span className={styles.agendaBad}>{badge}</span>}
                {selected.teams.map((t) => (
                  <span key={t} className={styles.agendaTeam}><span className={styles.dot} style={{ background: teamColors[t] ?? '#94a3b8' }} />{t}</span>
                ))}
              </div>
              <h3 className={styles.agendaDetailTitle}>
                {onSelect ? <button type="button" className={styles.agendaDetailTitleBtn} onClick={() => onSelect(selected.key)} title="Open the initiative" data-action="agenda_open_initiative" data-target-initiative-key={selected.key}>{selected.title}</button> : selected.title}
              </h3>
              <dl className={styles.agendaFacts}>
                <dt>Why today</dt><dd data-testid="agenda-why">{selected.reasons.join('. ')}</dd>
                <dt>Holds it</dt><dd>{selected.who ?? <span className={styles.muted}>nobody named</span>}</dd>
                <dt>Target</dt><dd>{selected.target ? fmtDayYear(parseDay(selected.target) as Date) : <span className={styles.muted}>none stated</span>}</dd>
                <dt>Status</dt><dd>{STATUS_LABELS[selected.initiative.status] ?? selected.initiative.status}</dd>
              </dl>
              {selected.dueQuestions.length > 0 && (
                <>
                  <h4 className={styles.agendaSub2}>To settle in the room</h4>
                  {selected.dueQuestions.map(ask)}
                </>
              )}
              {selected.laterQuestions.length > 0 && (
                <p className={styles.agendaFoot}>
                  {selected.laterQuestions.length} more question{selected.laterQuestions.length === 1 ? '' : 's'} on this item for a later date.{' '}
                  {onSelect && <button type="button" className={styles.linkBtn} onClick={() => onSelect(selected.key, 'questions')} data-action="agenda_open_initiative" data-target-initiative-key={selected.key}>See them</button>}
                </p>
              )}
              <h4 className={styles.agendaSub2}>The words behind it</h4>
              <EvidenceList evidence={quotes} onOpenSource={onOpenSource} aria-label={`Words behind ${selected.title}`} emptyNote="No quote recorded yet; Merlin adds the words behind each item on its next run." />
              <h4 className={styles.agendaSub2}>Summary</h4>
              <p className={styles.agendaSummary}>{selected.initiative.summary || <span className={styles.muted}>No summary recorded.</span>}</p>
              {onSelect && <p><button type="button" className={styles.agendaOpenBtn} onClick={() => onSelect(selected.key)} data-action="agenda_open_initiative" data-target-initiative-key={selected.key}>Open the initiative</button></p>}
            </article>
          )}
        </div>
      )}
    </section>
  );
}
